import { SxProps, Theme } from "@mui/material";
import {
  botMessageColors,
  botMessageColorsDark,
} from "../../../../colors/colors";

export const containerStyles: SxProps<Theme> = (theme: Theme) => {
  const palette =
    theme.palette.mode === "light" ? botMessageColors : botMessageColorsDark;
  return {
    background: palette.default.background,
    borderRadius: "16px",
    p: { xs: 1.8, sm: 2.1 },
    width: "32rem",
    maxWidth: "100%",
    color: palette.default.text,
    fontFamily: "Red Hat Display",
    fontWeight: 600,
    border: `1px solid ${theme.palette.divider}`,
  };
};

export const headerRowStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.2,
  marginBottom: 0.7,
};

export const headerDividerStyles: SxProps<Theme> = (theme: Theme) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  margin: "0 -16px 0",
});

export const titleStyles: SxProps<Theme> = (theme: Theme) => ({
  fontSize: { xs: "17px", sm: "18px" },
  lineHeight: 1.1,
  fontWeight: 700,
  color: theme.palette.text.primary,
});

export const subtitleStyles: SxProps<Theme> = (theme: Theme) => ({
  mt: 0.35,
  mb: 0.7,
  color: theme.palette.text.secondary,
  fontSize: { xs: "10px", sm: "11px" },
  fontWeight: 500,
  lineHeight: 1.35,
});

export const headerIconBubbleStyles: SxProps<Theme> = {
  width: 34,
  height: 34,
  borderRadius: "50%",
  background: "#4F46E5",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxShadow: "0 2px 8px rgba(79, 70, 229, 0.35)",
};

export const closeButtonStyles: SxProps<Theme> = (theme: Theme) => ({
  color: theme.palette.text.secondary,
  p: 0.2,
});

export const questionStyles: SxProps<Theme> = (theme: Theme) => ({
  mt: 2,
  mb: 0.6,
  fontSize: { xs: "12px", sm: "14px" },
  lineHeight: 1.2,
  fontWeight: 700,
  color: theme.palette.text.primary,
});

export const ratingQuestionStyles: SxProps<Theme> = (theme: Theme) => ({
  mt: 2,
  mb: 0.45,
  fontSize: { xs: "12px", sm: "14px" },
  lineHeight: 1.2,
  fontWeight: 700,
  color: theme.palette.text.primary,
});

export const starsRowStyles: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  alignItems: "center",
  width: "100%",
  gap: 0,
  mb: 0.4,
};

export const starButtonStyles = (active: boolean): SxProps<Theme> => ({
  p: 0.2,
  minWidth: "auto",
  lineHeight: 1,
  justifySelf: "center",
  borderRadius: "8px",
  color: active ? "#F5C106" : "#E0B21A",
  opacity: active ? 1 : 0.85,
});

export const starsLabelsRowStyles: SxProps<Theme> = (theme: Theme) => ({
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  alignItems: "center",
  width: "100%",
  color: theme.palette.text.secondary,
  fontSize: { xs: "10px", sm: "11px" },
  fontWeight: 600,
  mb: 0.85,
  "& span:first-of-type": {
    gridColumn: 1,
    justifySelf: "center",
    textAlign: "center",
  },
  "& span:last-of-type": {
    gridColumn: 5,
    justifySelf: "center",
    textAlign: "center",
  },
});

export const dividerStyles: SxProps<Theme> = (theme: Theme) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  margin: "10px 0 14px",
});

export const improveTitleStyles: SxProps<Theme> = (theme: Theme) => ({
  fontSize: { xs: "18px", sm: "20px" },
  lineHeight: 1.2,
  fontWeight: 700,
  color: theme.palette.text.primary,
  mb: 1.1,
});

export const tabsContainerStyles: SxProps<Theme> = (theme: Theme) => ({
  mt: 1.1,
  mb: 0.8,
  p: 0.35,
  borderRadius: "11px",
  border: `1px solid ${theme.palette.divider}`,
  display: "inline-flex",
  gap: 0.4,
});

export const tabButtonStyles = (active: boolean): SxProps<Theme> => (theme) => ({
  borderRadius: "8px",
  px: 1.6,
  py: 0.75,
  minWidth: 0,
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 700,
  color: active ? "#3D58D8" : theme.palette.text.secondary,
  backgroundColor: active ? "rgba(75, 107, 255, 0.12)" : "transparent",
  "&:hover": {
    backgroundColor: active ? "rgba(75, 107, 255, 0.16)" : theme.palette.action.hover,
  },
});

export const textFieldStyles: SxProps<Theme> = (theme: Theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor:
      theme.palette.mode === "light" ? "rgba(247, 248, 252, 0.85)" : "#212637",
    "& textarea": {
      fontSize: "12px",
      lineHeight: 1.45,
      paddingBottom: "46px",
    },
    "& fieldset": {
      borderColor: theme.palette.divider,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.text.secondary,
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4B6BFF",
    },
  },
});

export const counterStyles: SxProps<Theme> = (theme: Theme) => ({
  textAlign: "right",
  color: theme.palette.text.secondary,
  fontSize: "10px",
  ml: "auto",
  flexShrink: 0,
});

export const chipsRowStyles: SxProps<Theme> = {
  display: "flex",
  flexWrap: { xs: "wrap", sm: "nowrap" },
  gap: { xs: 1, sm: 0.75 },
  mb: 1.1,
  width: "100%",
};

export const chipStyles =
  (selected: boolean): SxProps<Theme> =>
  (theme: Theme) => ({
    borderRadius: "12px",
    px: 2.1,
    py: 0.9,
    border: `1px solid ${selected ? "#6B63FF" : "#D6D1F5"}`,
    color: selected ? "#5B51E8" : "#5F57D9",
    backgroundColor: selected
      ? "rgba(107, 99, 255, 0.10)"
      : theme.palette.background.paper,
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "none",
    whiteSpace: "nowrap",
    minWidth: "unset",
    flex: { xs: "0 1 auto", sm: 1 },
    justifyContent: "center",
    "& .MuiButton-startIcon": {
      marginRight: "6px",
      marginLeft: 0,
      color: "inherit",
    },
    "&:hover": {
      borderColor: "#6B63FF",
      backgroundColor: selected
        ? "rgba(107, 99, 255, 0.14)"
        : "rgba(107, 99, 255, 0.06)",
    },
  });

export const submitButtonStyles: SxProps<Theme> = (theme: Theme) => ({
  borderRadius: "12px",
  color: theme.palette.buttons.textColor,
  width: "100%",
  mt: 1,
  textTransform: "none",
  fontSize: { xs: "14px", sm: "15px" },
  fontWeight: 700,
  border: "1px solid transparent",
  transition: "background-color 0.2s ease-in-out, opacity 0.2s ease",
  background: "#6B63FF",
  minHeight: "48px",
  "&:hover": {
    background: "#5B51E8",
  },
  "&.Mui-disabled": {
    opacity: 1,
    color: "rgba(255,255,255,0.55)",
    background: "rgba(0,0,0,0.10)",
  },
});

export const issueTitleStyles: SxProps<Theme> = (theme: Theme) => ({
  fontSize: { xs: "12px", sm: "14px" },
  lineHeight: 1.25,
  fontWeight: 700,
  color: theme.palette.text.primary,
  mt: 1.2,
  mb: 0.55,
});

export const attachRowStyles: SxProps<Theme> = {
  position: "absolute",
  left: 14,
  right: 14,
  bottom: 10,
  display: "flex",
  alignItems: "center",
  gap: 1,
  pointerEvents: "none",
};

export const attachButtonStyles: SxProps<Theme> = (theme: Theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.8,
  color: theme.palette.text.secondary,
  fontSize: "12px",
  fontWeight: 500,
  cursor: "pointer",
  userSelect: "none",
  maxWidth: "55%",
  pointerEvents: "auto",
});

export const attachFileNameStyles: SxProps<Theme> = (theme: Theme) => ({
  color: theme.palette.text.primary,
  fontSize: "10px",
  maxWidth: "35%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export const attachErrorStyles: SxProps<Theme> = {
  color: "#E5484D",
  fontSize: "11px",
  mt: -0.35,
  mb: 0.45,
};

export const submitHintStyles: SxProps<Theme> = (theme: Theme) => ({
  mt: 0.6,
  textAlign: "center",
  color: theme.palette.text.secondary,
  fontSize: "12px",
  fontWeight: 600,
});

export const privacyRowStyles: SxProps<Theme> = (theme: Theme) => ({
  mt: 1.05,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.7,
  color: theme.palette.text.secondary,
  fontSize: "11px",
  fontWeight: 600,
});

export const errorMessageStyles: SxProps<Theme> = (theme: Theme) => ({
  color: "red",
  fontSize: "0.85rem",
  marginY: "0.4rem",
});
